import { useRef } from "react";

import { DecisionEnvironment } from "src/clients/jobs";
import { TestRunProgress } from "src/clients/router";
import {
  fetchJobSourcesV2,
  pollJobRunV2,
  useCreateJobSourceV2,
  useCreateOrFetchTestRunJob,
  useRunJobV2,
  useStopRunV2,
} from "src/jobs/api/queries";
import {
  JobConfigurationExecutionMode,
  JobRateLimitInterval,
  JobRun,
} from "src/jobs/types";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { logger } from "src/utils/logger";

type TestRunOnJobsArgs = {
  datasetId: string;
  limitType: "concurrency" | "rate-limit";
  executionMode: JobConfigurationExecutionMode;
  executionConcurrency: number;
  rateLimit: number;
  rateLimitInterval: JobRateLimitInterval;
};

export const useTestRunOnJobs = () => {
  const { workspace, flow, version } = useAuthoringContext();
  const { mutateAsync: createOrFetchJob } = useCreateOrFetchTestRunJob(
    workspace.base_url!,
    flow.id,
  );

  const jobData = useRef<{ jobId: string; runId: string } | null>(null);

  const { mutateAsync: createSource } = useCreateJobSourceV2(
    workspace.base_url!,
  );

  const { mutateAsync: runJob } = useRunJobV2(workspace.base_url!, flow.id);
  const { mutateAsync: stopJobRun } = useStopRunV2(workspace.base_url!);

  const stopTestRunOnJobs = async () => {
    if (!jobData.current) {
      return;
    }

    try {
      await stopJobRun({ ...jobData.current });
    } catch (error) {
      logger.error("error stopping job run", error);
    } finally {
      jobData.current = null;
    }
  };

  const pollJobRunInProgress = async (
    callback: (progress: TestRunProgress, jobRun: JobRun) => void,
  ): Promise<JobRun | null> => {
    if (!jobData.current) {
      return Promise.resolve(null);
    }

    return await pollJobRunV2(
      workspace.base_url!,
      jobData.current.jobId,
      jobData.current.runId,
      (jobRun) => {
        callback(
          {
            total: jobRun.total_rows_count,
            successful: jobRun.success_rows_count,
            failed: jobRun.failed_rows_count,
            ignored: jobRun.ignored_rows_count,
            output_mismatch: 0,
          },
          jobRun,
        );
      },
    );
  };

  const startTestRunOnJobs = async ({
    datasetId,
    limitType,
    executionMode,
    executionConcurrency,
    rateLimit,
    rateLimitInterval,
  }: TestRunOnJobsArgs) => {
    const job = await createOrFetchJob({
      flow_version_id: version.id,
    });

    jobData.current = null;

    const sources = await fetchJobSourcesV2(workspace.base_url!, job.id);
    const matchingSource = await (async () => {
      const exitingSource = sources.find(
        (source) =>
          source.configuration.type === "dataset" &&
          source.configuration.dataset_id === datasetId,
      );
      if (exitingSource) {
        return exitingSource;
      }
      return await createSource({
        name: `Test Run on Jobs ${datasetId}`,
        job_id: job.id,
        configuration: {
          type: "dataset",
          dataset_id: datasetId,
        },
      });
    })();

    const configurationOverride =
      limitType === "concurrency"
        ? {
            execution_mode: executionMode,
            execution_concurrency: executionConcurrency,
            rate_limit: null,
            rate_limit_interval: null,
          }
        : {
            execution_mode: executionMode,
            rate_limit: rateLimit,
            rate_limit_interval: rateLimitInterval,
            execution_concurrency: null,
          };

    const run = await runJob({
      jobId: job.id,
      environment: DecisionEnvironment.TEST,
      source_id: matchingSource.id,
      configuration_override: configurationOverride,
    });

    jobData.current = { jobId: job.id, runId: run.id };

    logger.log("job run", run);

    return run;
  };

  return {
    startTestRunOnJobs,
    stopTestRunOnJobs,
    pollJobRunInProgress,
    setCurrentJobRun: (jobRun: JobRun) => {
      jobData.current = { jobId: jobRun.job_id, runId: jobRun.id };
    },
  };
};
